"use client"

import { Eye, EyeSlash, MagnifyingGlassMini } from "@medusajs/icons"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"

const inputBaseStyles =
  "caret-ui-fg-base bg-ui-bg-field hover:bg-ui-bg-field-hover shadow-borders-base placeholder-ui-fg-muted text-ui-fg-base transition-fg relative w-full appearance-none rounded-md outline-none focus-visible:shadow-borders-interactive-with-active disabled:text-ui-fg-disabled disabled:!bg-ui-bg-disabled disabled:placeholder-ui-fg-disabled disabled:cursor-not-allowed aria-[invalid=true]:!shadow-borders-error invalid:!shadow-borders-error"

const inputVariants = cva(
  cn(
    inputBaseStyles,
    "[&::--webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
  ),
  {
    variants: {
      size: {
        base: "txt-compact-small h-8 px-2 py-1.5",
        small: "txt-compact-small h-7 px-2 py-1",
      },
    },
    defaultVariants: {
      size: "base",
    },
  }
)

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> &
  VariantProps<typeof inputVariants>

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size = "base", ...props }, ref) => {
    const [typeState, setTypeState] = React.useState(type)
    const isPassword = type === "password"
    const isSearch = type === "search"

    React.useEffect(() => {
      if (isPassword && typeState === "password" && type === "text") {
        setTypeState("text")
      }
      if (isPassword && typeState === "text" && type === "password") {
        setTypeState("password")
      }
    }, [type, typeState, isPassword])

    return (
      <div className="relative">
        <input
          ref={ref}
          type={isPassword ? typeState : type}
          className={cn(
            inputVariants({ size }),
            {
              "pl-8": isSearch && size === "base",
              "pr-8": isPassword && size === "base",
              "pl-7": isSearch && size === "small",
              "pr-7": isPassword && size === "small",
            },
            className
          )}
          {...props}
        />
        {isSearch && (
          <div
            role="img"
            className={cn(
              "text-ui-fg-muted pointer-events-none absolute bottom-0 left-0 flex items-center justify-center",
              { "h-8 w-8": size === "base", "h-7 w-7": size === "small" }
            )}
          >
            <MagnifyingGlassMini />
          </div>
        )}
        {isPassword && (
          <div
            className={cn(
              "absolute bottom-0 right-0 flex items-center justify-center border-l",
              { "h-8 w-8": size === "base", "h-7 w-7": size === "small" }
            )}
          >
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setTypeState(typeState === "password" ? "text" : "password")}
              className="text-ui-fg-muted hover:text-ui-fg-subtle flex h-full w-full items-center justify-center px-3"
            >
              {typeState === "password" ? <Eye /> : <EyeSlash />}
            </button>
          </div>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input, inputVariants }
